import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FaFile, FaFolder, FaChevronDown, FaChevronRight, FaCheck, FaMinus } from 'react-icons/fa6';
import instance from "../../../services/baseService";
import toast from "react-hot-toast";
import { toastService } from '../../../utils/toastService';
import { MAX_FILE_SELECTION } from '../../../constants/common';

const FolderListModal = ({
  show,
  onHide,
  client_id,
  folder_id,
  data,
  gstrFormName,
  year,
  month,
  subCategories = [],
  onSubmitSuccess,
}) => {
  // Function to check if a node or its descendants contain any files
  const hasFiles = (node) => {
    if (node.type === 'file') {
      return true;
    }

    if (node.children && Array.isArray(node.children)) {
      return node.children.some(child => hasFiles(child));
    }

    return false;
  };

  // Function to filter out folders that don't contain files
  const filterFoldersWithFiles = (node) => {
    // If it's a file, always include it
    if (node.type === 'file') {
      return node;
    }

    // If it's a folder, check if it has files in its hierarchy
    if (node.type === 'folder') {
      // First, recursively filter children
      const filteredChildren = node.children && Array.isArray(node.children)
        ? node.children
          .map(child => filterFoldersWithFiles(child))
          .filter(child => child !== null) // Remove null entries
        : [];

      // If after filtering, there are no children, check if this folder itself should be included
      // Only include this folder if it has files somewhere in its hierarchy
      if (filteredChildren.length > 0 || hasFiles(node)) {
        return {
          ...node,
          children: filteredChildren
        };
      }
    }

    // If folder has no files in its hierarchy, return null to exclude it
    return null;
  };

  // Handle the case where data is an array (from API response) and apply filtering
  const rootData = useMemo(() => {
    if (Array.isArray(data)) {
      return data
        .map(folder => filterFoldersWithFiles(folder))
        .filter(folder => folder !== null); // Only keep folders that have files
    }

    return data ? [filterFoldersWithFiles(data)] : [];
  }, [data]);

  const childMap = useMemo(() => {
    const map = {};
    function traverse(node) {
      if (!map[node.id]) map[node.id] = [];
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => {
          map[node.id].push(child.id);
          traverse(child);
        });
      }
    }
    if (rootData) {
      rootData.forEach(rootNode => traverse(rootNode))
    }
    return map;
  }, [rootData]);

  // State management
  const [checked, setChecked] = useState({});
  const [selectedSubCat, setSelectedSubCat] = useState('');
  const [loading, setLoading] = useState(false);
  const [commonNote, setCommonNote] = useState('');
  const [error, setError] = useState("");
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Calculate selected files count
  const selectedFilesCount = useMemo(() => {
    return Object.keys(checked).filter(id => checked[id]).length;
  }, [checked]);

  // Check if Sub Category is required (not for statement3)
  const isSubCategoryRequired = gstrFormName !== 'statement3';

  // Calculate if there are expanded folders
  const hasExpandedFolders = useMemo(() => expandedItems.size > 0, [expandedItems]);

  // Enhanced accordion toggle with better debugging
  const handleAccordionToggle = useCallback((itemId) => {
    console.log('Toggle called for:', itemId);
    console.log('Current expanded items before toggle:', Array.from(expandedItems));

    setExpandedItems(prev => {
      const newSet = new Set(prev);
      const wasExpanded = newSet.has(itemId);

      if (wasExpanded) {
        newSet.delete(itemId);
        console.log(`Collapsed: ${itemId}`);
      } else {
        newSet.add(itemId);
        console.log(`Expanded: ${itemId}`);
      }

      console.log('New expanded items after toggle:', Array.from(newSet));
      return newSet;
    });
  }, [expandedItems]);

  // Reset state when modal is shown
  useEffect(() => {
    if (show) {
      setExpandedItems(new Set());
      setSelectedSubCat('');
      setCommonNote('');
      setError('');
    }
  }, [show]);

  // Initialize checked state for files
  useEffect(() => {
    if (!rootData) return;

    const init = {};
    function traverse(node) {
      if (node.type === 'file') {
        init[node.id] = false;
      }
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    }
    rootData.forEach(rootNode => traverse(rootNode))
    setChecked(init);
  }, [rootData]);

  // Create node map for quick lookup
  const idToNodeMap = useMemo(() => {
    const map = {};
    function traverse(node) {
      map[node.id] = node;
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    }
    if (rootData) {
      rootData.forEach(rootNode => traverse(rootNode))
    }
    return map;
  }, [rootData]);
  // Enhanced height calculation
  const getContainerHeight = useMemo(() => {
    if (!hasExpandedFolders) return 'auto';

    const baseHeight = 200;
    const additionalHeight = Math.min(expandedItems.size * 50, 300);
    return `${baseHeight + additionalHeight}px`;
  }, [hasExpandedFolders, expandedItems.size]);



  // Get all file IDs from a folder (including nested folders)
  const getFileIdsFromFolder = useCallback((folderId) => {
    const fileIds = [];

    // Only get direct children, not all descendants
    if (childMap[folderId]) {
      childMap[folderId].forEach(childId => {
        const node = idToNodeMap[childId];
        if (node && node.type === 'file') {
          fileIds.push(childId);
        }
      });
    }

    return fileIds;
  }, [childMap, idToNodeMap]);

  // Check folder selection state (all selected, none selected, or partial)
  const getFolderSelectionState = useCallback((folderId) => {
    const fileIds = getFileIdsFromFolder(folderId);

    if (fileIds.length === 0) return 'none';

    const selectedCount = fileIds.filter(id => checked[id]).length;

    if (selectedCount === 0) return 'none';
    if (selectedCount === fileIds.length) return 'all';
    return 'partial';
  }, [checked, getFileIdsFromFolder]);

  // Handle folder select all/none
  const handleFolderSelectAll = useCallback((folderId) => {
    const fileIds = getFileIdsFromFolder(folderId);
    const currentSelectionState = getFolderSelectionState(folderId);

    const newChecked = { ...checked };

    // If any files are selected in this folder, deselect all
    if (currentSelectionState === 'all' || currentSelectionState === 'partial') {
      fileIds.forEach(id => {
        newChecked[id] = false;
      });
    } else {
      // Only select if no files are currently selected
      const currentSelectedCount = Object.keys(checked).filter(key => checked[key]).length;
      const availableSlots = MAX_FILE_SELECTION - currentSelectedCount;

      if (availableSlots <= 0) {
        toastService.error(`Maximum ${MAX_FILE_SELECTION} files can be selected`);
        return;
      }

      // Select up to available slots
      const filesToSelect = fileIds.slice(0, availableSlots);

      filesToSelect.forEach(id => {
        newChecked[id] = true;
      });

      // Show warning if not all files could be selected
      if (filesToSelect.length < fileIds.length) {
        const remaining = fileIds.length - filesToSelect.length;
        toastService.error(`Only ${filesToSelect.length} files selected. ${remaining} files skipped due to ${MAX_FILE_SELECTION}-file limit.`);
      }
    }

    setChecked(newChecked);
  }, [checked, getFileIdsFromFolder, getFolderSelectionState]);

  // Handle file checkbox changes
  const handleCheck = id => {
    const newChecked = { ...checked };
    const isChecked = !checked[id];

    // Check if trying to select more than 50 files
    if (isChecked) {
      const currentSelectedCount = Object.keys(checked).filter(key => checked[key]).length;
      if (currentSelectedCount >= MAX_FILE_SELECTION) {
        toastService.error(`Maximum ${MAX_FILE_SELECTION} files can be selected`);
        return;
      }
    }

    newChecked[id] = isChecked;
    setChecked(newChecked);
  };



  // Enhanced renderTree with custom accordion implementation and select all feature
  const renderTree = useCallback((node, parentKey = '') => {
    const isExpanded = expandedItems.has(node.id);

    return (
      <div key={node.id} className="tree-node mb-1">
        {node.type === 'folder' ? (
          <div className="folder-container">
            <div
              className="folder-header d-flex align-items-center justify-content-between py-2 px-2 rounded"
              style={{
                backgroundColor: isExpanded ? '#f8f9fa' : 'transparent',
                border: '1px solid transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.borderColor = '#dee2e6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isExpanded ? '#f8f9fa' : 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              <div
                className="d-flex align-items-center flex-grow-1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAccordionToggle(node.id);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="me-2">
                  {isExpanded ?
                    <FaChevronDown className="text-primary" size={12} /> :
                    <FaChevronRight className="text-primary" size={12} />
                  }
                </div>
                <FaFolder className="me-2 text-warning" />
                <span className="fw-medium">{node.name}</span>
              </div>

              {/* Select All Button */}
              <div className="select-all-container">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center px-2 py-1 no-hover"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFolderSelectAll(node.id);
                  }}
                  style={{
                    fontSize: '0.75rem',
                    border: '1px solid #dee2e6',
                    backgroundColor: 'white'
                  }}
                >
                  {(() => {
                    const selectionState = getFolderSelectionState(node.id);
                    const fileCount = getFileIdsFromFolder(node.id).length;

                    if (selectionState === 'all') {
                      return (
                        <>
                          <FaCheck className="me-1" size={10} />
                          <span>All ({fileCount})</span>
                        </>
                      );
                    } else if (selectionState === 'partial') {
                      const selectedCount = getFileIdsFromFolder(node.id).filter(id => checked[id]).length;
                      return (
                        <>
                          <FaMinus className="me-1" size={10} />
                          <span>{selectedCount}/{fileCount}</span>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <span>Select All Files ({fileCount})</span>
                        </>
                      );
                    }
                  })()}
                </Button>
              </div>
            </div>

            {isExpanded && (
              <div
                className="folder-content ms-3 border-start border-2 border-light ps-3 mt-2"
                style={{
                  animation: 'slideDown 0.2s ease-out',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}
              >
                {node.children && Array.isArray(node.children) &&
                  node.children.map(child => renderTree(child, node.id))
                }
              </div>
            )}
          </div>
        ) : (
          <div className="file-item d-flex align-items-center py-2 px-2 rounded">
            <Form.Check
              type="checkbox"
              id={`chk-${node.id}`}
              className="me-2 d-flex justify-content-center"
              checked={checked[node.id] || false}
              onChange={() => handleCheck(node.id)}
            />
            <FaFile className="me-2 text-secondary" />
            <span className="text-truncate">
              {node.file_name || node?.file_details?.file_name || 'Unnamed File'}
            </span>
          </div>
        )}
      </div>
    );
  }, [expandedItems, checked, handleAccordionToggle, handleFolderSelectAll, getFolderSelectionState, getFileIdsFromFolder]);

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // Gather selected files with file_key
    const selectedFileKeys = Object.keys(checked)
      .filter(id => checked[id] && idToNodeMap[id]?.type === 'file')
      .map(id => {
        const node = idToNodeMap[id];
        return {
          file_key: node.file_key || '',
          file_name: node.file_name || node?.file_details?.file_name || 'Unnamed File',
          file_size: node?.file_details?.file_size || 0
        };
      })
      .filter(item => item.file_key); // Remove items without file_key

    if (selectedFileKeys.length === 0) {
      setError('Please select at least one file.');
      setLoading(false);
      return;
    }

    // Only validate sub category if it's required
    if (isSubCategoryRequired && !selectedSubCat) {
      setError('Please select a sub category.');
      setLoading(false);
      return;
    }

    if (!commonNote.trim()) {
      setError('Please enter a note.');
      setLoading(false);
      return;
    }

    // Build payload based on API requirements
    const payload = {
      file_keys: selectedFileKeys,
      client_id: client_id,
      notes: commonNote,
      module: gstrFormName, // gstr1, gstr3b, refund, gstr9, gstr9c, statement3
    };

    // Add section_type only if sub category is required and selected
    if (isSubCategoryRequired && selectedSubCat) {
      payload.section_type = selectedSubCat;
    }

    // Add year and month only if module is not "refund" or "statement3"
    if (gstrFormName !== 'refund' && gstrFormName !== 'statement3') {
      payload.year = year;
      payload.month = month;
    }

    // Add folder_id if module is "refund" or "statement3"
    if (gstrFormName === 'refund' || gstrFormName === "statement3") {
      payload.folder_id = folder_id;
    }

    console.log('Payload to submit:', payload);

    try {
      const res = await instance.post('/api/v1/push/upload/files', payload);

      // Show success toast
      toast.success(res.data?.message || 'Files uploaded successfully!');

      if (onSubmitSuccess) onSubmitSuccess(res);
      onHide();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred while uploading files.';

      // Show error toast
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Add CSS for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
          max-height: 0;
        }
        to {
          opacity: 1;
          transform: translateY(0);
          max-height: 500px;
        }
      }
      
      .folder-header:hover {
        background-color: #f8f9fa !important;
        border-color: #dee2e6 !important;
      }
      
      .file-item:hover {
        background-color: #f8f9fa !important;
      }

      .select-all-container .btn:hover {
        background-color: #e9ecef !important;
        border-color: #adb5bd !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Don't render if no data
  if (!rootData) {
    return null;
  }

  return (
    <Modal
      id="folder-list-modal"
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
      enforceFocus={false}
      keyboard={false}
      className='border-0'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Import - {gstrFormName}
          {(year || month) && (
            <>
              {' ('}
              {year && year}
              {year && month && '-'}
              {month && month}
              {')'}
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* File Selection Counter and Warning */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">
              Selected Files: {selectedFilesCount}/{MAX_FILE_SELECTION}
            </span>
          </div>
        </div>

        {/* Enhanced Tree Display */}
        <div className="mb-3">
          <div
            className="tree-container"
            style={{
              height: getContainerHeight,
              minHeight: '150px',
              maxHeight: '400px',
              overflowY: hasExpandedFolders ? 'auto' : 'visible',
              border: '1px solid #dee2e6',
              borderRadius: '0.375rem',
              padding: '0.75rem',
              transition: 'height 0.3s ease-in-out, max-height 0.3s ease-in-out',
              backgroundColor: '#fafbfc'
            }}
          >
            {rootData && rootData.map((rootNode) => renderTree(rootNode))}
          </div>

        </div>

        {/* Conditionally render Sub Category section */}
        {isSubCategoryRequired && (
          <Form.Group className="mb-3">
            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedSubCat}
              onChange={e => setSelectedSubCat(e.target.value)}
            >
              <option value="">Select...</option>
              {Object.entries(subCategories || {}).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Note <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter a note for selected files"
            value={commonNote}
            onChange={e => setCommonNote(e.target.value)}
          />
        </Form.Group>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || (isSubCategoryRequired && !selectedSubCat) || !commonNote.trim()}
          className='fw-bold'
        >
          {loading ? <Spinner animation="border" size="sm" /> : 'Submit'}
        </Button>
        <Button variant="outline-primary" onClick={onHide} disabled={loading} className='fw-bold no-hover' >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FolderListModal;